import { Heading1 } from '@blueprint/ui';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
    EditSavedSearchBanner,
    isShowEditSavedSearchBanner
} from '@zoopla/listing-search/banners/edit-saved-search';
import { useGetMapViewUrl } from '@zoopla/listing-search/use-map-view-url';
import { useOptimizelyExperiment } from '@zoopla/optimizely';
import { totalResultsTextSelector } from '@zoopla/redux-reducer-search-results';
import * as React from 'react';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { MapIcon } from './MapIcon';
import {
    SearchResultsHeaderWrapper,
    SearchResultsTotalText,
    StyledDivider,
    StyledFooter,
    StyledFooterCell
} from './SearchResultsHeader.styles';

const LSRP_SIDEBAR_MEDIA_QUERY = `@media only screen and (min-width: 1140px)`;

const SectionThatAppearsWhenNoSideBar = styled.div(
    () => css`
        ${LSRP_SIDEBAR_MEDIA_QUERY} {
            display: none;
        }
    `
);

const Heading: FC<React.PropsWithChildren<{ pageTitle: string }>> = ({
    pageTitle
}) => {
    const [variation] = useOptimizelyExperiment('example_a_b_test');
    const shouldAddColon = 'variation_2' === variation;
    return (
        <Heading1 data-testid={`results-title`} isRegular>
            {pageTitle}
            {shouldAddColon ? ':' : ''}
        </Heading1>
    );
};

const HeadingWithOptimizely = Heading;

export type SearchResultsHeaderProps = {
    pageTitle: string;
    isEditSavedSearchBannerVisible: boolean;
    userAlertId: string | null;
    previousSavedSearchUri: string | null;
    savedSearchAndAlertsUri: string | undefined;
    isLoggedIn: boolean | null;
    sortOrderDropdown: React.ReactElement;
    outcode?: string;
};

export const SearchResultsHeader: FC<
    React.PropsWithChildren<SearchResultsHeaderProps>
> = ({
    pageTitle,
    isEditSavedSearchBannerVisible,
    userAlertId,
    previousSavedSearchUri,
    savedSearchAndAlertsUri,
    isLoggedIn,
    sortOrderDropdown
}) => {
    const totalResultsText = useSelector(totalResultsTextSelector);

    const [showEditSavedSearchBanner, setShowEditSavedSearchBanner] =
        useState(false);

    useEffect(() => {
        if (!isEditSavedSearchBannerVisible) {
            setShowEditSavedSearchBanner(false);
        } else if (isLoggedIn) {
            const showEditSavedSearch = isShowEditSavedSearchBanner(
                userAlertId,
                previousSavedSearchUri,
                savedSearchAndAlertsUri
            );

            setShowEditSavedSearchBanner(!!showEditSavedSearch);
        }
    }, [
        isLoggedIn,
        isEditSavedSearchBannerVisible,
        userAlertId,
        previousSavedSearchUri,
        savedSearchAndAlertsUri
    ]);

    const mapIconLinkGenericParams = useGetMapViewUrl();

    return (
        <SearchResultsHeaderWrapper data-testid="search-results-header-control">
            {showEditSavedSearchBanner ? (
                <SectionThatAppearsWhenNoSideBar data-testid="search-results-header_edit-saved-search-cta">
                    <EditSavedSearchBanner />
                </SectionThatAppearsWhenNoSideBar>
            ) : null}

            <HeadingWithOptimizely pageTitle={pageTitle} />

            <StyledFooter>
                <StyledFooterCell>
                    <SearchResultsTotalText data-testid="total-results">
                        {totalResultsText}
                    </SearchResultsTotalText>
                    <StyledDivider />
                    <MapIcon href={mapIconLinkGenericParams} />
                </StyledFooterCell>
                <StyledFooterCell>{sortOrderDropdown}</StyledFooterCell>
            </StyledFooter>
        </SearchResultsHeaderWrapper>
    );
};
